import type { Role } from "@/types/db";

/**
 * Who each route is for, stated rather than inherited.
 *
 * HQ-ROUTE-ROLE-001. 49 of HQ's 154 routes sat under a single
 * <ProtectedRoute><Layout/></ProtectedRoute>, which asks only "is this an
 * active employee who is not a kiosk". Every other role passed — accountant,
 * c_suite and investor included — unless an inner route happened to add its own
 * allow or permission. The audit's direct sweep reached 36 of 42 employee
 * routes that way. Nothing was wrong with most of those pages being open to
 * most staff; what was wrong is that no one had said so, so the boundary was
 * whatever the nesting happened to produce and no test could tell a deliberate
 * choice from an oversight.
 *
 * This file is that statement. Enforcement stays in ProtectedRoute, and the
 * database keeps its own rules — a route policy is a navigational courtesy, not
 * a security boundary, and the two are allowed to disagree only in the
 * direction of the database being stricter.
 */

/** Everyone who works here. Excludes kiosk (a shared terminal, redirected to
 *  its own page) and investor (an outside party — see INVESTOR below). */
export const STAFF: readonly Role[] = [
  "employee", "manager", "admin", "super_admin", "c_suite", "accountant", "sales_rep",
];

/**
 * An investor is not staff. They are outside the company and see a deliberately
 * narrow surface: their own profile, and chat — where hq_can_dm already governs
 * who may open a conversation with whom.
 *
 * No investor-specific pages exist yet, and there are no investor accounts in
 * production, so this tightens nothing that anyone is currently using. It stops
 * the next investor account from reaching /pto, /orders and /reviews by
 * default, which is what happens today.
 */
export const INVESTOR_ROUTES: readonly string[] = ["/", "/profile", "/chat"];

export type Audience =
  /** Reachable without a session at all. */
  | { kind: "public" }
  /** Any active employee who works here. */
  | { kind: "staff" }
  /** Staff, plus investors — the small shared surface. */
  | { kind: "staff+investor" }
  /** Only these roles. */
  | { kind: "roles"; roles: readonly Role[] }
  /** Gated by the route's own permission= or allow= prop in App.tsx. */
  | { kind: "declared-inline" };

/**
 * Every route in App.tsx, and who it is for.
 *
 * A route missing from this map fails route-policy.test.ts. That is the point:
 * adding a page to HQ should require saying who it is for, once, here.
 */
export const ROUTE_POLICY: Record<string, Audience> = {
  // ── Reachable before signing in ─────────────────────────────────────────
  "/login": { kind: "public" },
  "/register": { kind: "public" },
  "/forgot-password": { kind: "public" },
  "/reset-password": { kind: "public" },
  "/welcome": { kind: "public" },
  "/deactivated": { kind: "public" },

  // ── Its own world ───────────────────────────────────────────────────────
  "/kiosk": { kind: "roles", roles: ["kiosk", "admin", "super_admin"] },
  "/onboarding": { kind: "staff" },

  // ── Shared with investors ───────────────────────────────────────────────
  "/": { kind: "staff+investor" },
  "/profile": { kind: "staff+investor" },
  "/chat": { kind: "staff+investor" },

  // ── Everyone who works here ─────────────────────────────────────────────
  "/time": { kind: "staff" },
  "/sops": { kind: "staff" },
  "/documents": { kind: "staff" },
  "/resources": { kind: "staff" },
  "/announcements": { kind: "staff" },
  "/directory": { kind: "staff" },
  "/pto": { kind: "staff" },
  "/crm": { kind: "staff" },
  "/crm/contacts": { kind: "staff" },
  "/crm/pipelines": { kind: "staff" },
  "/crm/calendar": { kind: "staff" },
  "/tasks": { kind: "staff" },
  "/tasks/:boardId": { kind: "staff" },
  "/orders": { kind: "staff" },
  "/rituals": { kind: "staff" },
  "/kudos": { kind: "staff" },
  "/suggestions": { kind: "staff" },
  "/dev-requests": { kind: "staff" },
  "/checklists": { kind: "staff" },
  "/training": { kind: "staff" },
  "/one-on-ones": { kind: "staff" },
  "/discipline/:id": { kind: "staff" },
  "/goals": { kind: "staff" },
  "/reviews": { kind: "staff" },
  // HQ-HIRE-READY-001: new hires waiting for approval. The page only ever lists
  // people who report to the viewer (an admin sees all), and the function behind
  // it decides that, so this only keeps the menu entry for those who could use it.
  "/new-hires": { kind: "roles", roles: ["manager", "admin", "super_admin"] },
  "/handbook": { kind: "staff" },
  "/polls": { kind: "staff" },
  "/mission": { kind: "staff" },
  "/wins": { kind: "staff" },
  "/jobs": { kind: "staff" },
  "/huddle": { kind: "staff" },
  "/priority": { kind: "staff" },
  "/priority/*": { kind: "staff" },

  // ── Parents whose children carry the real gate ──────────────────────────
  // ── D2C ─────────────────────────────────────────────────────────────────
  // Gated by the route's own permission= prop. Anyone holding
  // d2c.journey.view sees it; nobody else does, whatever their role.
  "/d2c/customer-journey": { kind: "declared-inline" },

  "/admin": { kind: "declared-inline" },
  "employees": { kind: "declared-inline" },
  "time": { kind: "declared-inline" },
  "*": { kind: "public" },
};

/** Does this role reach this route, per the policy above? */
export function audienceAdmits(audience: Audience, role: Role | null | undefined): boolean {
  if (!role) return audience.kind === "public";
  switch (audience.kind) {
    case "public": return true;
    case "staff": return STAFF.includes(role);
    case "staff+investor": return STAFF.includes(role) || role === "investor";
    case "roles": return audience.roles.includes(role);
    // The route states its own rule in App.tsx; ProtectedRoute applies it.
    case "declared-inline": return true;
  }
}

/**
 * The policy for a concrete pathname, matching parameterised routes.
 * Returns null when the path is not a declared route — which the catch-all
 * handles, and which must never be treated as permission.
 */
export function policyFor(pathname: string): Audience | null {
  if (ROUTE_POLICY[pathname]) return ROUTE_POLICY[pathname];
  for (const [pattern, audience] of Object.entries(ROUTE_POLICY)) {
    if (!pattern.includes(":")) continue;
    const rx = new RegExp("^" + pattern.replace(/:[^/]+/g, "[^/]+") + "$");
    if (rx.test(pathname)) return audience;
  }
  return null;
}

/** May this role open this pathname? Unknown paths are left to the router. */
export function canEnter(pathname: string, role: Role | null | undefined): boolean {
  const policy = policyFor(pathname);
  if (!policy) return true;
  return audienceAdmits(policy, role);
}
